/**
 * GDriveFolderPickerPanel
 *
 * The connect-time folder-selection step from
 * docs/features/gdrive-folder-scoping/SPEC.md §5.1 — shown immediately after
 * a fresh GDrive connect while the channel is still UNCONFIGURED (fail-closed
 * on the backend until at least one folder is chosen).
 *
 * 2026-07-31 positioning-bug postmortem: this used to be `GDriveFolderPickerModal`,
 * first built on our Radix `Modal`, then (still broken, per live product-owner
 * testing) rebuilt as a plain `createPortal` full-screen backdrop with its own
 * `backdrop-blur` + scroll-lock. BOTH versions hid/mispositioned the native
 * Google Picker. The actual root cause (see the "Google Picker — viewport-fixed
 * positioning overrides" comment block in `index.css`): the Picker SDK injects
 * `.picker-dialog`/`.picker-dialog-bg` directly into `<body>` with
 * `position: fixed`, and ANY competing full-viewport overlay open at the same
 * time (backdrop-filter, its own scroll-lock, etc.) throws that off — it
 * doesn't matter whether the overlay is a Radix Dialog or a hand-rolled one.
 * The composer's browse Picker (`MediaSection.tsx`'s `openGDrivePicker`) never
 * had this problem because it opens from a plain `<button>` with NOTHING else
 * competing for the viewport.
 *
 * So this component is now a genuinely INLINE panel: no `fixed`, no
 * `backdrop-filter`/blur, no portal, no scroll-lock. It renders as a normal
 * block wherever it's placed in the tree (e.g. directly under the just-
 * connected channel's row in `ChannelSettings.tsx`, or inline in the
 * composer's media toolbar in `MediaSection.tsx`) and its "Specific folders"
 * button calls the Picker's `open()` directly — structurally identical to
 * the composer's working pattern. All actual merge/save logic lives in
 * `useGDriveFolderPicker` (shared with `GDriveFoldersSection`'s "Add" flow,
 * which calls that hook directly with no panel at all).
 *
 * Access-mode extension (docs/features/gdrive-folder-scoping/ACCESS-MODE.md):
 * this panel only ever shows while the channel is still UNCONFIGURED (all
 * three call sites gate it that way), so it offers BOTH choices side by
 * side — "Specific folders" (scoped, via the Picker above) and "Entire
 * Drive" (`{ mode: 'all' }`, no Picker, no confirm needed — there's nothing
 * to broaden FROM when starting unconfigured).
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useGDriveFolderPicker } from '@/hooks/useGDriveFolderPicker';
import type { UpdateGDriveFoldersMutation } from '@/hooks/useGDriveImport';
import type { GDriveSharedFolder } from '@/types/gdrive';

interface GDriveFolderPickerPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId: number;
  /** Folders already shared — merged (deduped) with newly picked ones. */
  existingFolders?: GDriveSharedFolder[];
  onSaved?: (folders: GDriveSharedFolder[]) => void;
  /** Shared mutation instance — see `useGDriveFolderPicker`'s race-safety note. */
  mutation?: UpdateGDriveFoldersMutation;
}

export const GDriveFolderPickerPanel = ({
  open,
  onOpenChange,
  channelId,
  existingFolders,
  onSaved,
  mutation,
}: GDriveFolderPickerPanelProps) => {
  const { t } = useTranslation('settings');

  const { openPicker, shareEntireDrive, isSaving, pickerError } = useGDriveFolderPicker({
    channelId,
    existingFolders,
    mutation,
    onSaved: (folders) => {
      onSaved?.(folders);
      onOpenChange(false);
    },
  });

  if (!open) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {t('gdrive_folder_step_title', { defaultValue: 'Choose folders to share' })}
        </h3>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200 focus:outline-none shrink-0"
          aria-label={t('settings_modal_cancel', { defaultValue: 'Close' })}
        >
          <Icon name={ICONS.CLOSE} size="xs" ariaHidden />
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-soft-mint p-3">
        <Icon name={ICONS.FOLDER} size="sm" color="text-dark-teal" ariaHidden className="mt-0.5 flex-shrink-0" />
        <p className="text-xs text-dark-teal">
          {t('gdrive_folder_step_nudge', {
            defaultValue:
              "Choose the folders to share (e.g. your \"Shareable content [Brand]\" folder and its subfolders). Your team will only be able to see and import files inside the folders you pick here.",
          })}
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3">
        <Icon name={ICONS.WARNING} size="sm" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-800">
          {t('gdrive_folder_step_unconfigured_warning', {
            defaultValue: "Until you choose at least one folder, this connection can't be used to import files.",
          })}
        </p>
      </div>

      {/* Leaf-folder guidance — the product model shares exactly the
          folder(s) picked here, not their parent/ancestor. */}
      <div className="flex items-start gap-2">
        <Icon name={ICONS.INFO} size="sm" color="text-gray-400" ariaHidden className="mt-0.5 flex-shrink-0" />
        <p className="text-xs text-gray-500">
          {t('gdrive_folder_step_leaf_guidance', {
            defaultValue:
              'Pick the folders that directly contain your files (e.g. Bilder, Videos, Blog) — not an umbrella parent. Files inside sub-folders aren\'t shared unless you add that sub-folder too.',
          })}
        </p>
      </div>

      {pickerError && <p className="text-xs text-red-600">{pickerError.message}</p>}

      <div className="flex flex-wrap justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={isSaving}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
        >
          {t('gdrive_folder_step_skip', { defaultValue: "I'll do this later" })}
        </button>
        <Button variant="outline" onClick={shareEntireDrive} loading={isSaving}>
          <Icon name={ICONS.GLOBE} size="sm" ariaHidden className="mr-1.5" />
          {t('gdrive_folder_step_choose_all', { defaultValue: 'Entire Drive' })}
        </Button>
        <Button onClick={openPicker} loading={isSaving}>
          <Icon name={ICONS.FOLDER} size="sm" ariaHidden className="mr-1.5" />
          {t('gdrive_folder_step_choose_scoped', { defaultValue: 'Specific folders' })}
        </Button>
      </div>
    </div>
  );
};
